import { getCorrectnessActionGroups, all_streets } from '@/helpers/constants'
import { setHsSelectedAction } from '@/store/gamestate/gamestate'

function getEvPotLossGroups() {
  return [
    {
      id: 'ev_pot_ok',
      clr: 'var(--clr-white)',
      to: n => n <= 0.005,
      from: n => n >= 0,
    },
    {
      id: 'ev_pot_worse',
      clr: 'var(--clr-yellow)',
      to: n => n < 0.05,
      from: n => n > 0.005,
    },
    {
      id: 'ev_pot_worst',
      clr: 'var(--clr-error)',
      to: n => n < Infinity,
      from: n => n >= 0.05,
    },
  ]
}

function getEvLossGroups() {
  return [
    {
      id: 'ev_ok',
      clr: 'var(--clr-white)',
      to: n => n <= 0.01,
      from: n => n >= 0,
    },
    {
      id: 'ev_worse',
      clr: 'var(--clr-yellow)',
      to: n => n < 0.5,
      from: n => n > 0.01,
    },
    {
      id: 'ev_worst',
      clr: 'var(--clr-error)',
      to: n => n < Infinity,
      from: n => n >= 0.5,
    },
  ]
}

function getEvGroup({ options, num }) {
  const res = options.find(i => i.to(num) && i.from(num)) || options[0]
  return res
}

// Outstanding    95%
// Excellent    92%
// Well played    88%
// Good    85%
// Fair    82%
// Marginal    75%
// Inaccurate    70%
// Needs work    60%
// Blunder    <60%
function getTrainerScoreGroups() {
  return [
    {
      id: 'outstanding',
      texts: ['outstanding', 'superb', 'exceptional', 'remarkable', 'astonishing'],
      clr: 'var(--clr-primary)',
      icon: 'mdi-check-all',
      to: n => n <= 1,
      from: n => n >= 0.95,
    },
    {
      id: 'excellent',
      texts: ['excellent', 'great', 'fantastic', 'terrific', 'marvelous'],
      clr: 'var(--clr-primary)',
      icon: 'mdi-check-all',
      to: n => n < 0.95,
      from: n => n >= 0.92,
    },
    {
      id: 'well_played',
      texts: ['well_played', 'skilled', 'savvy', 'proficient'],
      clr: 'var(--clr-primary)',
      icon: 'mdi-check-all',
      to: n => n < 0.92,
      from: n => n >= 0.88,
    },
    {
      id: 'good',
      texts: ['good', 'solid', 'acceptable', 'decent', 'respectable'],
      clr: 'var(--clr-primary)',
      icon: 'mdi-check',
      to: n => n < 0.88,
      from: n => n >= 0.85,
    },
    {
      id: 'fair',
      texts: ['fair', 'adequate', 'sufficient', 'median'],
      clr: 'var(--clr-primary)',
      icon: 'mdi-check',
      to: n => n < 0.85,
      from: n => n >= 0.82,
      // real 0.90
    },
    {
      id: 'marginal',
      texts: ['marginal', 'average', 'mediocre', 'ordinary'],
      clr: 'var(--clr-yellow)',
      icon: 'mdi-information-outline',
      to: n => n < 0.82,
      from: n => n >= 0.75,
      // real 0.875
    },
    {
      id: 'inaccurate',
      texts: ['inaccurate', 'errant', 'imprecise', 'incorrect'],
      clr: 'var(--clr-yellow)',
      icon: 'mdi-information-outline',
      to: n => n < 0.75,
      from: n => n >= 0.7,
    },
    {
      id: 'needs_work',
      texts: ['needs_work', 'improvable', 'deficient', 'lacking'],
      clr: 'var(--clr-yellow)',
      icon: 'mdi-information-outline',
      to: n => n < 0.7,
      from: n => n >= 0.6,
    },
    {
      id: 'blunder',
      texts: ['blunder', 'gaffe', 'misstep', 'slip_up'],
      clr: 'var(--clr-error)',
      icon: 'mdi-alert',
      to: n => n < 0.6,
      from: n => n >= -1,
    },
  ]
}

function getTrainerScoreGroup(num) {
  const opts = getTrainerScoreGroups()
  const fallback = {
    id: 'na',
    clr: 'var(--clr-grey-2)',
    icon: 'mdi-progress-alert',
  }
  const res = opts.find(i => i.to(num) && i.from(num)) || fallback
  return res
}

function getTrainerStats({ stats = null, action = null, hands = 0, stats_hand = null } = {}) {
  // console.log(action)
  const correctness = action?.correctness

  const total_hands = (stats?.total_hands || 0) + (hands || 0)
  const inaccuracy_moves = (stats?.inaccuracy_moves || 0) + (correctness === 'inaccuracy' ? 1 : 0)
  const best_moves = (stats?.best_moves || 0) + (correctness === 'best_move' ? 1 : 0)
  const correct_moves = (stats?.correct_moves || 0) + (correctness === 'correct_move' ? 1 : 0)
  const wrong_moves = (stats?.wrong_moves || 0) + (correctness === 'wrong_move' ? 1 : 0)
  const blunder_moves = (stats?.blunder_moves || 0) + (correctness === 'blunder' ? 1 : 0)

  const total_ev_loss = (stats?.total_ev_loss || 0) + (action ? Number(action.ev_loss) : 0)
  const total_gto_score = (stats?.total_gto_score || 0) + (action ? Number(action.gto_score) : 0)

  const total_bad_res = blunder_moves + wrong_moves + inaccuracy_moves
  const total_good_res = best_moves + correct_moves
  const total_moves = total_bad_res + total_good_res

  const avg_gto_score = total_gto_score / (total_moves || 1)
  const good_percentage = total_good_res / (total_moves || 1)
  const average_ev_mistake = total_ev_loss / (total_bad_res || 1)

  const average_ev_loss = total_hands ? total_ev_loss / (total_hands || 1) : 0

  // hand score is hands action scores per move but session gtoscore is hands scores per hands
  // example: each hand finish screen gto score (combined hand-actions scores / hand-moves)
  const total_hands_gto_score =
    (stats?.total_hands_gto_score || 0) + (stats_hand ? stats_hand.avg_gto_score : 0)
  const gto_score_per_hand = total_hands ? total_hands_gto_score / (total_hands || 1) : 0

  const gto_score_group = getTrainerScoreGroup(avg_gto_score)

  const res = {
    total_hands,
    total_moves,
    total_gto_score,
    avg_gto_score,
    gto_score_group,
    gto_score_per_hand,
    // gto_score_per_hand_group,
    total_hands_gto_score,
    total_bad_res,
    total_good_res,
    total_ev_loss,
    good_percentage,
    average_ev_mistake,
    average_ev_loss,
    inaccuracy_moves,
    best_moves,
    correct_moves,
    wrong_moves,
    blunder_moves,
    // game_hands,
  }
  // console.log(res, '-------------STATS')
  // debugger
  return res
}

// not used
function getHspotsCorrectness({ history_spots, hero_position }) {
  const cgs = getCorrectnessActionGroups()
  const allHeroSpots = history_spots.filter(i => i.position === hero_position)
  const allHeroActions = allHeroSpots
    .map(spot => {
      const action = spot.available_actions.find(a => a.is_selected)
      const selected_action = {
        correctness: 'na',
        correctness_group: cgs.na,
        ...action,
      }
      const r = {
        spot_id: spot.id,
        street: spot.street,
        selected_action,
      }
      return r
    })
    .filter(i => i.selected_action)

  if (!allHeroActions.length) {
    console.warn('getHspotsCorrectness---No hero actions', { history_spots, hero_position })
    // return null
    return {
      total_ev_loss: 0,
      correctness_group: cgs.na,
    }
  }

  const best_move = allHeroActions.filter(
    i => i?.selected_action?.correctness_group?.res_id === 'best_move',
  )
  const correct_move = allHeroActions.filter(
    i => i?.selected_action?.correctness_group?.res_id === 'correct_move',
  )
  const inaccuracy = allHeroActions.filter(
    i => i?.selected_action?.correctness_group?.res_id === 'inaccuracy',
  )
  const wrong_move = allHeroActions.filter(
    i => i?.selected_action?.correctness_group?.res_id === 'wrong_move',
  )
  const blunder = allHeroActions.filter(
    i => i?.selected_action?.correctness_group?.res_id === 'blunder',
  )

  const ok = [...correct_move, ...best_move]
  const not_ok = [...blunder, ...wrong_move]
  // const inaccuracy = inaccuracy

  // worst ever spot
  const worstSpot = not_ok.length
    ? not_ok[0]
    : inaccuracy.length
      ? inaccuracy[0]
      : ok.length
        ? ok[0]
        : null

  const correctness_group = worstSpot?.selected_action?.correctness_group || cgs.na
  // debugger

  const total_ev_loss = allHeroActions.reduce((acc, i) => acc + (i.ev_loss || 0), 0)
  const res = {
    total_ev_loss,
    correctness_group,
  }
  return res
}

function getStreetCorrectness({ street_spots }) {
  const cgs = getCorrectnessActionGroups()
  // let streetSpots = street_spots.filter(i => i.selected_action)

  const fallbackAction = {
    correctness: 'na',
    correctness_group: {
      ...cgs.na,
      icon: '',
    },
  }
  const fallbackSpot = {
    spot_id: street_spots[0].id,
    street: street_spots[0].street,
    selected_action: fallbackAction,
  }

  const streetSpots = street_spots
    .map(spot => {
      const action = spot.available_actions.find(a => a.is_selected && a.correctness !== 'na')
      const selected_action = {
        ...fallbackAction,
        ...(action || {}),
      }
      const r = {
        spot_id: spot.id,
        street: spot.street,
        selected_action,
      }
      return r
    })
    .filter(i => i.selected_action.correctness !== 'na')
  // .filter(i => !i.selected_action)

  const allStreetActions = streetSpots.reduce((a, b) => [...a, b.selected_action], [])
  const correctnessRanks = allStreetActions.map(i => i.correctness_group.rank)
  const maxRank = Math.max(...correctnessRanks)

  let worstSpot = streetSpots.find(i => i.selected_action.correctness_group.rank === maxRank)
  worstSpot = worstSpot || fallbackSpot
  const worstAction = worstSpot?.selected_action || fallbackAction
  const correctness_group = worstAction?.correctness_group || cgs.na

  const total_ev_loss = streetSpots.reduce((a, b) => a + Number(b.selected_action.ev_loss), 0)
  const total_gto_score = streetSpots.reduce((a, b) => a + Number(b.selected_action.gto_score), 0)
  const avg_gto_score = total_gto_score / (streetSpots.length || 1)
  const gto_score_group = getTrainerScoreGroup(avg_gto_score)
  // console.log(spot.street)
  // console.log(streetSpots, street_spots, gto_score_group, avg_gto_score)
  // debugger
  const res = {
    total_ev_loss,
    avg_gto_score,
    gto_score_group,
    correctness_group,
    worst_spot_id: worstSpot.spot_id,
    street_na: !streetSpots.length,
    // street_na: streetSpots.every(s => s.selected_action.correctness === 'na'),
    // street_na: true,
  }
  // debugger
  return res
}

function getHandCorrectness({ history_spots, hero_position }) {
  const streets = all_streets
    .map(street => {
      const hSpots = history_spots.filter(
        i => i.type === 'player' && i.street === street && i.position === hero_position,
      )
      let correctness = null
      // debugger
      if (hSpots.length) {
        correctness = getStreetCorrectness({ street_spots: hSpots })
      }
      const res = {
        street_id: street,
        correctness,
      }
      return res
    })
    .filter(s => s.correctness)
  // console.log(streets)

  const { total_ev_loss, correctness_group } = getHspotsCorrectness({
    history_spots,
    hero_position,
  })
  const game_na = streets.every(s => s.correctness.street_na)
  const res = {
    id: 'fullgame',
    total_ev_loss,
    correctness_group,
    streets,
    game_na,
  }
  return res
}

function getHandStats({ history_spots, hero_position }) {
  let stats = getTrainerStats()
  history_spots.forEach(s => {
    if (s.position === hero_position) {
      const action = s.available_actions.find(a => a.is_selected)
      if (action?.correctness && action?.correctness !== 'na') {
        stats = getTrainerStats({ stats, action })
      }
    }
  })
  return stats
}

function updateHsSpotActions({ history_spots, spot, action, slider_actions, key, getNext }) {
  history_spots = JSON.parse(JSON.stringify(history_spots))
  const hSpots = setHsSelectedAction({
    history_spots,
    spot,
    action,
    key,
    getNext,
  })
  hSpots[spot.id] = {
    ...hSpots[spot.id],
    slider_actions: slider_actions || hSpots[spot.id].slider_actions,
  }
  return history_spots
}

export {
  getTrainerStats,
  getHandCorrectness,
  getTrainerScoreGroup,
  getTrainerScoreGroups,
  updateHsSpotActions,
  getHandStats,
  getEvPotLossGroups,
  getEvLossGroups,
  getEvGroup,
}
